import { useCallback, useEffect, useMemo, useState } from 'react'
import { usePermissions } from '@/hooks/usePermissions'
import { PERMISSIONS, PERMISSION_DENIED_HINT } from '@/lib/permissions'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ExternalLink, Lock, Wrench } from 'lucide-react'
import * as planningApi from '../../../../lib/api/v2/planning'
import type {
  ClientOrderDto, ClientOrderLineDto, HardwareSuggestionDto, ProjectDetailDto, SupplierDto, SupplierOrderLineDto,
} from '../../../../lib/api/v2/planning'
import { SUPPLIER_LINE_DESCRIPTION_MAX, SUPPLIER_LINE_TITLE_MAX } from '../../../../lib/api/v2/planning'
import { cn } from '../../../../lib/utils'
import {
  computeRegistryDiff, fullClientLineText, isCommandable, isDiffEmpty, isLineRemovable, liveFreeLines,
  orphanedTrackedLines, reconcileClientOrder, supplierLineTextOf, type DraftFreeLine,
} from '../client-order-helpers'
import { LINE_STATUS_LABEL } from '../sidebar/supplier-orders-helpers'
import { formatCalendarDay } from '../planning-helpers'

/**
 * The « registre » (SOLY-PO-001 — rework, MS-14 enriched) — the reconciliation/diff surface, strictly
 * separated from tracking: the CLIENT ORDER on the left (a signed quote OR a direct sale — never
 * « devis »), the supplier order list on the right, side by side. The whole right column is a LOCAL
 * draft: ticking a commandable line stages an add, unticking stages a remove, a free line stages a
 * free add — NO per-click server call. The docked « Valider » footer commits the whole diff in one
 * reconcile call. MS-14 adds: full descriptions, per-line quantities, the single remove point for
 * committed free lines, and the « Quincaillerie détectée » suggestion panel.
 */
export function OrdersRegistryPage() {
  const { id } = useParams<{ id: string }>()
  return (
    <div className="flex h-screen flex-col" data-testid="orders-registry-page">
      {id && <Registry projectId={id} />}
    </div>
  )
}

/** Seed the desired set from the live, source-matched tracked lines (the current tracking truth). */
function seedDesiredSourceIds(supplierLines: SupplierOrderLineDto[]): Set<string> {
  const ids = new Set<string>()
  for (const l of supplierLines) {
    if (l.sourceOrderLineId && l.lineStatus !== 'Cancelled') ids.add(l.sourceOrderLineId)
  }
  return ids
}

/** Stable draft key for a hardware suggestion so checking/unchecking toggles the same staged line. */
const hardwareKey = (s: HardwareSuggestionDto) => `hw:${s.model.toLowerCase()}|${s.color.toLowerCase()}`
const hardwareDescription = (s: HardwareSuggestionDto) => `Poignées ${s.model}${s.color ? ` ${s.color}` : ''}`.trim()

function Registry({ projectId }: { projectId: string }) {
  const navigate = useNavigate()
  const [project, setProject] = useState<ProjectDetailDto | null>(null)
  const [clientOrder, setClientOrder] = useState<ClientOrderDto | null>(null)
  const [lines, setLines] = useState<SupplierOrderLineDto[] | null>(null)
  const [hardware, setHardware] = useState<HardwareSuggestionDto[]>([])
  const [mutating, setMutating] = useState(false)
  const { can } = usePermissions()
  // SOLY-PERM-001 — `orders:read` opens the registre, `orders:manage` lets it be edited. Every
  // control here already reads `busy`; read-only rides that channel and says why, once, at the top.
  const readOnly = !can(PERMISSIONS.ORDERS_MANAGE)
  const busy = mutating || readOnly
  const [adding, setAdding] = useState(false)

  // ── Local draft (the staged diff) ──────────────────────────────────────────
  const [desiredSourceIds, setDesiredSourceIds] = useState<Set<string>>(new Set())
  const [desiredQuantities, setDesiredQuantities] = useState<Map<string, number | null>>(new Map())
  const [draftFreeLines, setDraftFreeLines] = useState<DraftFreeLine[]>([])
  const [removedDetachedLineIds, setRemovedDetachedLineIds] = useState<Set<string>>(new Set())

  const load = useCallback(async () => {
    try {
      const [detail, order, supplierLines, hw] = await Promise.all([
        planningApi.getProjectDetail(projectId).then((d) => d).catch(() => null),
        planningApi.getClientOrder(projectId),
        planningApi.getSupplierLines(projectId),
        planningApi.getHardwareSuggestions(projectId).catch(() => [] as HardwareSuggestionDto[]),
      ])
      setProject(detail)
      setClientOrder(order)
      setLines(supplierLines)
      setHardware(hw)
      // Re-seed the draft from the freshly fetched tracking truth, clearing any pending stage.
      setDesiredSourceIds(seedDesiredSourceIds(supplierLines))
      setDesiredQuantities(new Map())
      setDraftFreeLines([])
      setRemovedDetachedLineIds(new Set())
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Commande client indisponible')
      setClientOrder({ salesOrderId: null, orderLines: [], nothingToOrderAt: null })
      setLines([])
      setHardware([])
      setDesiredSourceIds(new Set())
      setDesiredQuantities(new Map())
      setDraftFreeLines([])
      setRemovedDetachedLineIds(new Set())
    }
  }, [projectId])

  useEffect(() => { void load() }, [load])

  const ready = clientOrder !== null && lines !== null
  const { rows } = useMemo(
    () => ready ? reconcileClientOrder(clientOrder!.orderLines, lines!) : { rows: [], manual: [] },
    [ready, clientOrder, lines],
  )

  const diff = useMemo(
    () => computeRegistryDiff({
      clientLines: clientOrder?.orderLines ?? [],
      supplierLines: lines ?? [],
      desiredSourceIds,
      draftFreeLines,
      desiredQuantities,
      removedDetachedLineIds,
    }),
    [clientOrder, lines, desiredSourceIds, draftFreeLines, desiredQuantities, removedDetachedLineIds],
  )
  const dirty = !isDiffEmpty(diff)

  // Block accidental navigation away with a pending diff (browser back / refresh / tab close).
  useEffect(() => {
    if (!dirty) return
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirty])

  if (!ready) {
    return <p className="p-4 text-sm text-muted-foreground" data-testid="orders-registry-loading">Chargement…</p>
  }

  // ── Kind-aware client lines (discount/surcharge hidden, grouped by category) ──
  const visibleLines = clientOrder!.orderLines.filter((l) => l.kind !== 'discount' && l.kind !== 'surcharge')
  // A line set to Fabriquer or Aucun after it was put on the list still counts while it is on it.
  const commandableCount = clientOrder!.orderLines.filter((l) => isCommandable(l) || desiredSourceIds.has(l.id)).length
  const groups = groupByCategory(visibleLines)

  // Gauge: the denominator counts ONLY commandable lines (notes/services never count). The
  // desired set may carry orphaned source ids (pre-MS-07 amendments) — they must not inflate it.
  const clientLineIds = new Set(clientOrder!.orderLines.map((l) => l.id))
  const followed = [...desiredSourceIds].filter((sourceId) => clientLineIds.has(sourceId)).length
  const undecided = Math.max(0, commandableCount - followed)

  const toggle = (clientLine: ClientOrderLineDto, on: boolean) => {
    setDesiredSourceIds((prev) => {
      const next = new Set(prev)
      if (on) next.add(clientLine.id); else next.delete(clientLine.id)
      return next
    })
    setDesiredQuantities((prev) => {
      const next = new Map(prev)
      if (on) next.set(clientLine.id, clientLine.quantity); else next.delete(clientLine.id)
      return next
    })
  }

  const setStagedQty = (sourceId: string, quantity: number | null) =>
    setDesiredQuantities((prev) => new Map(prev).set(sourceId, quantity))

  const toggleHardware = (s: HardwareSuggestionDto) => {
    const key = hardwareKey(s)
    setDraftFreeLines((prev) => prev.some((l) => l.draftKey === key)
      ? prev.filter((l) => l.draftKey !== key)
      : [...prev, { draftKey: key, description: hardwareDescription(s), supplierId: null, quantity: null }])
  }

  const toggleRemoveDetached = (lineId: string) =>
    setRemovedDetachedLineIds((prev) => {
      const next = new Set(prev)
      if (next.has(lineId)) next.delete(lineId); else next.add(lineId)
      return next
    })

  const guardedNavigate = (to: string) => {
    if (dirty && !window.confirm('Des modifications ne sont pas validées. Quitter sans valider ?')) return
    navigate(to)
  }

  const validate = async () => {
    if (!dirty) { navigate(`/planning/projects/${projectId}`); return }
    setMutating(true)
    try {
      await planningApi.reconcileClientOrderLines(projectId, diff)
      toast.success('Liste de commande mise à jour')
      navigate(`/planning/projects/${projectId}`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Validation échouée')
    } finally {
      setMutating(false)
    }
  }

  const markNothing = async () => {
    setMutating(true)
    try {
      await planningApi.markNothingToOrder(projectId)
      toast.success('« Rien à commander » confirmé')
      await load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Action échouée')
    } finally {
      setMutating(false)
    }
  }

  const live = lines!.filter((l) => l.lineStatus !== 'Cancelled')
  const committedFree = liveFreeLines(lines!)
  // Orphaned tracked lines (source vanished on a pre-MS-07 amendment) are part of the list too —
  // they used to be silently hidden, which read as « la liste a disparu ».
  const orphans = orphanedTrackedLines(clientOrder!.orderLines, lines!)
  const listCount = followed + committedFree.length + draftFreeLines.length + orphans.length
  const checkedHardwareKeys = new Set(draftFreeLines.map((l) => l.draftKey))
  // A handle spec already covered by a live free line must not be re-proposed (else a re-check
  // after reload would create a duplicate — reviewed RISK). Match on the generated description.
  const orderedHardwareDescriptions = new Set(committedFree.map((l) => l.description.toLowerCase()))
  const poseDate = derivePoseDate(project)

  return (
    <>
      {/* ── Sticky top: back link + project-context header ───────────────────── */}
      <div className="flex-none border-b bg-background">
        <div className="mx-auto w-full max-w-5xl px-4 py-3">
          <button type="button" onClick={() => guardedNavigate(`/planning/projects/${projectId}`)}
            data-testid="orders-registry-back" className="text-sm text-muted-foreground hover:underline">
            ← Retour au chantier
          </button>
          {readOnly && (
            <p className="mt-1.5 rounded-md bg-muted px-2.5 py-1.5 text-[11px] text-muted-foreground"
              data-testid="orders-registry-readonly">
              Lecture seule — {PERMISSION_DENIED_HINT[PERMISSIONS.ORDERS_MANAGE]}
            </p>
          )}
          {project && (
            <div className="mt-2 flex items-start justify-between gap-3" data-testid="orders-registry-context">
              <div className="min-w-0">
                <h1 className="truncate text-base font-semibold">{project.clientName}</h1>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {project.reference && <span className="font-mono">{project.reference}</span>}
                  {project.reference && project.projectName ? ' · ' : ''}
                  {project.projectName}
                  {poseDate && <> · <span className="font-medium text-foreground">pose le {formatCalendarDay(poseDate)}</span></>}
                </p>
              </div>
              <a href={`/projects/${project.projectId}`} target="_blank" rel="noopener noreferrer"
                data-testid="orders-registry-open-project"
                className="inline-flex flex-none items-center gap-1 rounded border px-2 py-1 text-[11px] font-medium text-foreground hover:bg-muted">
                Ouvrir le chantier <ExternalLink className="h-3 w-3" aria-hidden />
              </a>
            </div>
          )}
        </div>
      </div>

      {/* ── Scrollable middle ────────────────────────────────────────────────── */}
      <div className="min-h-0 flex-1 overflow-auto">
        <div className="mx-auto w-full max-w-5xl space-y-4 p-4">
          <div>
            <h2 className="text-lg font-semibold">Préparer la liste de commande</h2>
            <p className="text-sm text-muted-foreground">Cochez les lignes de la commande client à commander, ajustez les quantités, ajoutez ce qui n'y figure pas — le suivi vit dans le Planning.</p>
          </div>

          {/* Anti-forget gauge: only COMMANDABLE lines count in the denominator. */}
          <div className="rounded-md bg-muted px-3 py-1.5 text-xs" data-testid="orders-registry-gauge">
            📋 {commandableCount} article{commandableCount > 1 ? 's' : ''} commandable{commandableCount > 1 ? 's' : ''} · <b>{followed} suivi{followed > 1 ? 's' : ''}</b>
            {undecided > 0 && <> · <b className="text-amber-700">{undecided} sans décision</b></>}
            {draftFreeLines.length > 0 && <> — {draftFreeLines.length} ajout{draftFreeLines.length > 1 ? 's' : ''} libre{draftFreeLines.length > 1 ? 's' : ''}</>}
          </div>

          <div className="flex flex-col gap-4 md:flex-row">
            {/* ── Left: the client order (grouped by category) ──────────────── */}
            <div className="min-w-0 flex-1 space-y-3" data-testid="orders-registry-quote">
              <div className="rounded bg-muted px-2 py-1 text-[11px] font-semibold text-muted-foreground">COMMANDE CLIENT</div>
              {visibleLines.length === 0 && <p className="text-xs text-muted-foreground">Pas de commande client signée.</p>}
              {groups.map((group) => (
                <div key={group.category} className="space-y-1" data-testid={`orders-registry-group-${slug(group.category)}`}>
                  <div className="px-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">{group.category}</div>
                  {group.lines.map((clientLine) => (
                    <ClientLineRow
                      key={clientLine.id} clientLine={clientLine}
                      tracked={rows.find((r) => r.clientLine.id === clientLine.id)?.tracked ?? null}
                      desired={desiredSourceIds.has(clientLine.id)}
                      busy={busy}
                      onToggle={(on) => toggle(clientLine, on)} />
                  ))}
                </div>
              ))}

              {/* ── Quincaillerie détectée (MS-14 item 4) — inférée, distincte des lignes signées ── */}
              {hardware.length > 0 && (
                <div className="space-y-1 rounded-md border border-amber-200 bg-amber-50/50 p-1.5" data-testid="orders-registry-hardware">
                  <div className="flex items-center gap-1.5 px-1 text-[10.5px] font-semibold uppercase tracking-wide text-amber-800">
                    <Wrench className="h-3 w-3" aria-hidden /> Quincaillerie détectée
                  </div>
                  {hardware.map((s) => (
                    <HardwareRow key={hardwareKey(s)} suggestion={s}
                      checked={checkedHardwareKeys.has(hardwareKey(s))}
                      already={orderedHardwareDescriptions.has(hardwareDescription(s).toLowerCase())}
                      busy={busy} onToggle={() => toggleHardware(s)} />
                  ))}
                </div>
              )}
            </div>

            {/* ── Right: the staged order list ──────────────────────────────── */}
            <div className="min-w-0 flex-1 space-y-1.5" data-testid="orders-registry-list">
              <div className="rounded bg-muted px-2 py-1 text-[11px] font-semibold text-muted-foreground">
                LISTE DE COMMANDE ({listCount})
              </div>
              {listCount === 0 && (
                <p className="text-xs text-muted-foreground">Rien encore — cochez à gauche ou ajoutez une ligne libre.</p>
              )}

              {/* Desired source lines (tracked-or-staged) — staged ones carry an editable quantity. */}
              {rows.filter((r) => desiredSourceIds.has(r.clientLine.id)).map(({ clientLine, tracked }) => (
                <div key={clientLine.id} className="flex items-start gap-2 rounded-md border px-2.5 py-1.5" data-testid={`orders-registry-item-${clientLine.id}`}>
                  <span className="min-w-0 flex-1">
                    <span className="block break-words text-sm">{tracked ? supplierLineTextOf(tracked) : fullClientLineText(clientLine)}</span>
                    <span className="block text-[11px] text-muted-foreground">
                      ↩ commande client{tracked?.supplierName ? ` · ${tracked.supplierName}` : ''}
                    </span>
                  </span>
                  {tracked
                    ? <span className="flex-none whitespace-nowrap pt-0.5 text-[11px] text-muted-foreground" data-testid={`orders-registry-qty-tracked-${clientLine.id}`}>{tracked.quantity != null ? `× ${tracked.quantity}` : '—'} · suivi</span>
                    : (
                      <span className="flex flex-none items-center gap-1.5">
                        <QtyInput value={desiredQuantities.has(clientLine.id) ? desiredQuantities.get(clientLine.id)! : clientLine.quantity}
                          onChange={(q) => setStagedQty(clientLine.id, q)} testId={`orders-registry-qty-${clientLine.id}`} disabled={busy} />
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9.5px] font-medium text-slate-700">à ajouter</span>
                      </span>
                    )}
                </div>
              ))}

              {/* Committed free lines (no source) — the registre is their single remove point (item 3). */}
              {committedFree.map((l) => {
                const removable = isLineRemovable(l)
                const staged = removedDetachedLineIds.has(l.id)
                return (
                  <div key={l.id} className={cn('flex items-start gap-2 rounded-md border px-2.5 py-1.5', staged && 'opacity-60')} data-testid={`orders-registry-free-${l.id}`}>
                    <span className="min-w-0 flex-1">
                      <span className={cn('block break-words text-sm', staged && 'line-through')}>{supplierLineTextOf(l)}</span>
                      <span className="block text-[11px] text-muted-foreground">libre{l.supplierName ? ` · ${l.supplierName}` : ''}</span>
                    </span>
                    <span className="flex flex-none items-center gap-1.5 whitespace-nowrap pt-0.5 text-[11px] text-muted-foreground">
                      {l.quantity != null ? `× ${l.quantity}` : '—'}
                      {removable
                        ? (staged
                          ? <button type="button" disabled={busy} onClick={() => toggleRemoveDetached(l.id)} data-testid={`orders-registry-free-undo-${l.id}`} className="rounded px-1 text-[10px] text-muted-foreground hover:text-foreground">annuler</button>
                          : <button type="button" disabled={busy} onClick={() => toggleRemoveDetached(l.id)} title="Retirer de la liste" data-testid={`orders-registry-free-remove-${l.id}`} className="rounded px-1 text-[11px] text-muted-foreground hover:text-destructive">✕</button>)
                        : <span className="flex items-center gap-0.5" title="déjà commandée — gérer depuis le suivi" data-testid={`orders-registry-free-locked-${l.id}`}><Lock className="h-3 w-3" aria-hidden /></span>}
                    </span>
                  </div>
                )
              })}

              {/* Orphaned tracked lines — the source order line vanished on a pre-MS-07 amendment.
                  They are real ordered/to-order items: shown with their status, never hidden. */}
              {orphans.length > 0 && (
                <div className="space-y-1 rounded-md border border-amber-200 bg-amber-50/50 p-1.5" data-testid="orders-registry-orphans">
                  <div className="px-1 text-[10.5px] font-semibold uppercase tracking-wide text-amber-800">
                    Suivies sans correspondance
                  </div>
                  <p className="px-1 text-[11px] text-amber-800/90">
                    La commande client a changé après la mise en suivi de ces lignes — vérifiez qu'elles sont toujours à commander.
                  </p>
                  {orphans.map((l) => {
                    const removable = isLineRemovable(l)
                    const staged = removedDetachedLineIds.has(l.id)
                    return (
                      <div key={l.id} className={cn('flex items-start gap-2 rounded-md border bg-background px-2.5 py-1.5', staged && 'opacity-60')} data-testid={`orders-registry-orphan-${l.id}`}>
                        <span className="min-w-0 flex-1">
                          <span className={cn('block break-words text-sm', staged && 'line-through')}>{supplierLineTextOf(l)}</span>
                          <span className="block text-[11px] text-muted-foreground">
                            {LINE_STATUS_LABEL[l.lineStatus]}{l.supplierName ? ` · ${l.supplierName}` : ''}
                          </span>
                        </span>
                        <span className="flex flex-none items-center gap-1.5 whitespace-nowrap pt-0.5 text-[11px] text-muted-foreground">
                          {l.quantity != null ? `× ${l.quantity}` : '—'}
                          {removable
                            ? (staged
                              ? <button type="button" disabled={busy} onClick={() => toggleRemoveDetached(l.id)} data-testid={`orders-registry-orphan-undo-${l.id}`} className="rounded px-1 text-[10px] text-muted-foreground hover:text-foreground">annuler</button>
                              : <button type="button" disabled={busy} onClick={() => toggleRemoveDetached(l.id)} title="Retirer de la liste" data-testid={`orders-registry-orphan-remove-${l.id}`} className="rounded px-1 text-[11px] text-muted-foreground hover:text-destructive">✕</button>)
                            : <span className="flex items-center gap-0.5" title="déjà commandée — gérer depuis le suivi" data-testid={`orders-registry-orphan-locked-${l.id}`}><Lock className="h-3 w-3" aria-hidden /></span>}
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Staged free lines (local, not yet committed) — editable quantity, removable. */}
              {draftFreeLines.map((fl) => (
                <div key={fl.draftKey} className="flex items-start gap-2 rounded-md border border-slate-300 bg-muted/60 px-2.5 py-1.5" data-testid={`orders-registry-draft-${fl.draftKey}`}>
                  <span className="min-w-0 flex-1">
                    <span className="block break-words text-sm">{supplierLineTextOf(fl)}</span>
                    <span className="block text-[11px] text-muted-foreground">libre · à ajouter</span>
                  </span>
                  <span className="flex flex-none items-center gap-1.5">
                    <QtyInput value={fl.quantity ?? null}
                      onChange={(q) => setDraftFreeLines((l) => l.map((x) => x.draftKey === fl.draftKey ? { ...x, quantity: q } : x))}
                      testId={`orders-registry-draft-qty-${fl.draftKey}`} disabled={busy} />
                    <button type="button" disabled={busy} onClick={() => setDraftFreeLines((l) => l.filter((x) => x.draftKey !== fl.draftKey))}
                      data-testid={`orders-registry-draft-remove-${fl.draftKey}`}
                      className="rounded px-1 text-[11px] text-muted-foreground hover:text-destructive">✕</button>
                  </span>
                </div>
              ))}

              {adding
                ? <FreeLineForm onAdd={(fl) => { setDraftFreeLines((l) => [...l, fl]); setAdding(false) }} onClose={() => setAdding(false)} />
                : <button type="button" disabled={busy} onClick={() => setAdding(true)} data-testid="orders-registry-add"
                    className="w-full rounded-md border border-dashed py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted">
                    ＋ Ligne libre — titre + description + quantité
                  </button>}
            </div>
          </div>
        </div>
      </div>

      {/* ── Sticky footer: pending diff + Valider ────────────────────────────── */}
      <div className="flex-none border-t bg-background">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-3 px-4 py-3">
          {live.length === 0 && draftFreeLines.length === 0 && desiredSourceIds.size === 0 && !clientOrder!.nothingToOrderAt ? (
            <button type="button" disabled={busy} data-testid="orders-registry-nothing"
              onClick={() => void markNothing()}
              className="text-xs text-muted-foreground underline">Rien à commander pour ce chantier</button>
          ) : (
            <span className="text-xs text-muted-foreground" data-testid="orders-registry-diff">
              {dirty ? formatDiff(diff) : 'Aucune modification en attente'}
            </span>
          )}
          <button type="button" data-testid="orders-registry-validate" disabled={busy}
            onClick={() => void validate()}
            className="rounded-md bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">
            {dirty ? 'Valider' : 'Valider → retour au Planning'}
          </button>
        </div>
      </div>
    </>
  )
}

/** Compact « +N à ajouter · −M à retirer » summary of the staged diff. */
function formatDiff(diff: planningApi.ReconcileClientOrderBody): string {
  const parts: string[] = []
  const additions = diff.adds.length + diff.freeLines.length
  if (additions > 0) parts.push(`+${additions} à ajouter`)
  if (diff.removeLineIds.length > 0) parts.push(`−${diff.removeLineIds.length} à retirer`)
  return parts.join(' · ')
}

// ── Quantity input (number, optional) ─────────────────────────────────────────
function QtyInput({ value, onChange, testId, disabled }: {
  value: number | null
  onChange: (v: number | null) => void
  testId: string
  disabled?: boolean
}) {
  return (
    <input type="number" min={0} step="any" inputMode="decimal" value={value ?? ''}
      onChange={(e) => {
        const raw = e.target.value.trim()
        if (raw === '') { onChange(null); return }
        const n = Number(raw)
        onChange(Number.isNaN(n) || n < 0 ? null : n)
      }}
      disabled={disabled} data-testid={testId} aria-label="Quantité" placeholder="Qté"
      className="w-16 flex-none rounded border px-1.5 py-0.5 text-right text-xs" />
  )
}

// ── Quincaillerie détectée — one inferred handle spec, rendered in the left column (MS-14 item 4) ──
function HardwareRow({ suggestion: s, checked, already, busy, onToggle }: {
  suggestion: HardwareSuggestionDto
  checked: boolean
  already: boolean
  busy: boolean
  onToggle: () => void
}) {
  const id = slug(`${s.model}-${s.color}`)
  return (
    <label className={cn('flex items-start gap-2 rounded bg-white/70 px-2 py-1 text-xs', already && 'opacity-60')} data-testid={`orders-registry-hardware-${id}`}>
      <input type="checkbox" checked={already || checked} disabled={busy || already}
        onChange={() => { if (!already) onToggle() }}
        data-testid={`orders-registry-hardware-check-${id}`} className="mt-0.5 h-4 w-4 flex-none" />
      <span className="min-w-0 flex-1 break-words">
        {s.model}{s.color ? <span className="text-muted-foreground"> · {s.color}</span> : null}
        {already && <span className="ml-1 rounded bg-emerald-100 px-1 py-0.5 text-[9px] text-emerald-700">déjà</span>}
      </span>
    </label>
  )
}

// ── One client-order line row (kind-aware) ────────────────────────────────────
export function ClientLineRow({ clientLine, tracked, desired, busy, onToggle }: {
  clientLine: ClientOrderLineDto
  tracked: SupplierOrderLineDto | null
  desired: boolean
  busy: boolean
  onToggle: (on: boolean) => void
}) {
  const detail = clientLine.description?.trim()
  const qtyLabel = clientLine.quantity != null ? `× ${clientLine.quantity}` : null

  // Made, nothing to buy, or a service nobody set to « Acheter »: read-only, « non commandable » tag.
  // Unless it is already on the list (qualified after it was ticked, S11): it can still be taken off.
  if (!isCommandable(clientLine) && !tracked && !desired) {
    return (
      <div className="flex items-start gap-2 rounded-md border bg-muted/30 px-2.5 py-1.5 opacity-70"
        data-testid={`orders-registry-line-${clientLine.id}`}>
        <span className="min-w-0 flex-1">
          <span className="block break-words text-sm">{clientLine.title}</span>
          {detail && <span className="block break-words text-[11px] text-muted-foreground">{detail}</span>}
        </span>
        {qtyLabel && <span className="flex-none whitespace-nowrap pt-0.5 text-[11px] text-muted-foreground" data-testid={`orders-registry-line-qty-${clientLine.id}`}>{qtyLabel}</span>}
        <span className="flex-none rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
          data-testid={`orders-registry-noncommandable-${clientLine.id}`}>
          non commandable
        </span>
      </div>
    )
  }

  // A tracked line that has been ordered (Assigned/Received) is LOCKED: it stays checked, disabled.
  const locked = tracked !== null && !isLineRemovable(tracked)

  return (
    <label className={cn('flex items-start gap-2 rounded-md border px-2.5 py-1.5', desired && 'opacity-80')}
      data-testid={`orders-registry-line-${clientLine.id}`}>
      <input type="checkbox" checked={desired || locked} disabled={busy || locked}
        onChange={(e) => onToggle(e.target.checked)}
        data-testid={`orders-registry-check-${clientLine.id}`}
        title={locked ? 'déjà commandée — gérer depuis le suivi' : (desired ? 'Retirer de la liste de commande' : 'Ajouter à la liste de commande')}
        className="mt-0.5 h-4 w-4 flex-none" />
      <span className="min-w-0 flex-1">
        <span className="block break-words text-sm">{clientLine.title}</span>
        {detail && <span className="block break-words text-[11px] text-muted-foreground">{detail}</span>}
      </span>
      {qtyLabel && <span className="flex-none whitespace-nowrap pt-0.5 text-[11px] text-muted-foreground" data-testid={`orders-registry-line-qty-${clientLine.id}`}>{qtyLabel}</span>}
      {locked && (
        <span className="flex flex-none items-center gap-1 pt-0.5 text-[10px] text-muted-foreground"
          data-testid={`orders-registry-locked-${clientLine.id}`} title="déjà commandée — gérer depuis le suivi">
          <Lock className="h-3 w-3" aria-hidden /> commandée
        </span>
      )}
    </label>
  )
}

interface CategoryGroup { category: string; lines: ClientOrderLineDto[] }

/** Groups lines by category, preserving line order within each group; first appearance fixes order. */
function groupByCategory(lines: ClientOrderLineDto[]): CategoryGroup[] {
  const order: string[] = []
  const byCat = new Map<string, ClientOrderLineDto[]>()
  for (const l of lines) {
    const cat = l.category?.trim() || 'Autres'
    if (!byCat.has(cat)) { byCat.set(cat, []); order.push(cat) }
    byCat.get(cat)!.push(l)
  }
  return order.map((category) => ({ category, lines: byCat.get(category)! }))
}

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')

/**
 * Tells the user their text is past what the line can store, instead of the two silent failures
 * this replaces: a maxLength attribute that swallows the tail of a paste, and the 500 the server
 * returned before it clamped (prod incident 2026-09-08). Renders nothing while the text fits.
 */
function OverLimitHint({ overBy, max, field, testId }: {
  overBy: number
  max: number
  field: string
  testId: string
}) {
  if (overBy <= 0) return null
  return (
    <p role="alert" data-testid={testId} className="px-0.5 text-[11px] text-destructive">
      {field} dépasse de {overBy} caractère{overBy > 1 ? 's' : ''} (max {max}).
    </p>
  )
}

// ── Free-line form (stages locally — NO server call) ──────────────────────────
export function FreeLineForm({ onAdd, onClose }: {
  onAdd: (line: DraftFreeLine) => void
  onClose: () => void
}) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [quantity, setQuantity] = useState('')
  const [suppliers, setSuppliers] = useState<SupplierDto[] | null>(null)
  const [supplierId, setSupplierId] = useState('')

  useEffect(() => { void planningApi.getSuppliers().then(setSuppliers).catch(() => setSuppliers([])) }, [])

  // Title and description are two columns on the line (V108) — they travel apart. Description is
  // NOT NULL server-side, so a free line with no detail repeats its title there.
  const trimmedTitle = title.trim()
  const trimmedDescription = description.trim()
  const titleOverBy = trimmedTitle.length - SUPPLIER_LINE_TITLE_MAX
  const descriptionOverBy = trimmedDescription.length - SUPPLIER_LINE_DESCRIPTION_MAX
  const tooLong = titleOverBy > 0 || descriptionOverBy > 0
  const parsedQty = () => {
    const raw = quantity.trim()
    if (raw === '') return null
    const n = Number(raw)
    return Number.isNaN(n) || n < 0 ? null : n
  }

  return (
    <div className="space-y-1 rounded-md border p-2" data-testid="orders-registry-add-form">
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre (ex: Quincaillerie Blum assortie)"
        className={cn('w-full rounded border px-2 py-1 text-xs', titleOverBy > 0 && 'border-destructive')}
        aria-invalid={titleOverBy > 0} data-testid="orders-registry-add-title" autoFocus />
      <OverLimitHint overBy={titleOverBy} max={SUPPLIER_LINE_TITLE_MAX} field="Le titre" testId="orders-registry-add-title-overflow" />
      <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optionnelle)"
        className={cn('w-full rounded border px-2 py-1 text-xs', descriptionOverBy > 0 && 'border-destructive')}
        aria-invalid={descriptionOverBy > 0} data-testid="orders-registry-add-desc" />
      <OverLimitHint overBy={descriptionOverBy} max={SUPPLIER_LINE_DESCRIPTION_MAX} field="La description" testId="orders-registry-add-desc-overflow" />
      <div className="flex gap-1">
        <input value={quantity} onChange={(e) => setQuantity(e.target.value)} type="number" min={0} step="any" inputMode="decimal"
          placeholder="Quantité (optionnelle)" className="w-32 rounded border px-2 py-1 text-xs" data-testid="orders-registry-add-qty" />
        <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className="min-w-0 flex-1 rounded border px-2 py-1 text-xs" data-testid="orders-registry-add-supplier">
          <option value="">Fournisseur pressenti (optionnel)</option>
          {(suppliers ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} data-testid="orders-registry-add-cancel" className="rounded px-2 py-1 text-xs text-muted-foreground">Annuler</button>
        <button type="button" disabled={!trimmedTitle || tooLong} data-testid="orders-registry-add-submit"
          onClick={() => onAdd({
            draftKey: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            title: trimmedTitle,
            description: trimmedDescription || trimmedTitle,
            supplierId: supplierId || null,
            quantity: parsedQty(),
          })}
          className="rounded bg-primary px-3 py-1 text-xs text-primary-foreground disabled:opacity-50">Ajouter</button>
      </div>
    </div>
  )
}

/** Pose date (yyyy-MM-dd) for the context header — the first scheduled Installation event, if any. */
function derivePoseDate(project: ProjectDetailDto | null): string | null {
  if (!project) return null
  const installation = project.installations.find((i) => i.phase === 1) ?? project.installations[0]
  if (!installation?.currentEventId) return null
  const event = project.events.find((e) => e.id === installation.currentEventId)
  return event ? event.startDate : null
}

export default OrdersRegistryPage
